import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { ALERT_PAGE_CAP, fetchStockWatch, type StockWatchRow } from './icmAlertApi';
import '../../backlog/backlog.css';
import './icmAlerts.css';

/**
 * Inventory Management → Alert → Stock Watch.
 *
 * Every item that currently has stock on hand in this division — "what is on
 * the shelf".  Ported from ALPACA `Items_ListView_Copy_StockWatch`
 * (criteria `[StockQty] > 0`), which is read-only: the XAF model sets
 * AllowNew / AllowEdit / AllowDelete / AllowLink to False and
 * HideDetailViewController disables the row-click detail.  So this form has no
 * New / Edit / Delete and no detail view — Refresh and Export only.
 *
 * The Stock Qty column is legacy's `Items.DivisionStock` (the division's
 * received-minus-disposed quantity), computed set-based in
 * `AI_ICM_StockWatch_List_SP` rather than per row in C#.
 */
export default function StockWatchPage() {
  const notify = useNotify();

  const [rows, setRows] = useState<StockWatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchStockWatch();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
      if (page.total > page.rows.length) {
        notify.info(`Showing the first ${ALERT_PAGE_CAP.toLocaleString()} of ${page.total.toLocaleString()} items — narrow the search to see the rest.`);
      }
    } catch {
      setError('Failed to load stock watch.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // notify is a stable service handle; excluded so Refresh isn't re-created.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.description ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.catalog ?? '').toLowerCase().includes(t) ||
      (r.storageNames ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Column set + order from the LIVE ALPACA model (BTLIMS.Web/Model.xafml:
  // Items_ListView_Copy_StockWatch) — Item · Description · Vendor · Catalog# ·
  // StockQty · ItemCode · PackUnit · Storage.
  const cols: AppColumn<StockWatchRow>[] = [
    { field: 'itemName',     header: 'Item',        width: 240, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'description',  header: 'Description', width: 260, text: r => r.description ?? '—', render: r => r.description ?? '—' },
    { field: 'vendorName',   header: 'Vendor',      width: 150, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'catalog',      header: 'Catalog #',   width: 130, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'stockQty',     header: 'Stock Qty',   width: 100, type: 'number', align: 'right', text: r => String(r.stockQty), value: r => r.stockQty },
    { field: 'itemCode',     header: 'Item Code',   width: 120, text: r => r.itemCode ?? '—', render: r => r.itemCode ?? '—' },
    { field: 'packUnit',     header: 'Pack Unit',   width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'storageNames', header: 'Storage',     width: 160, text: r => r.storageNames ?? '—', render: r => r.storageNames ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Alert</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Stock Watch</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-stock-watch" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'stock-watch', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search item, code, description, vendor, storage…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} item(s) in stock</span>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<StockWatchRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No items with stock on hand."
            />
          )}
        </div>
      </div>
    </div>
  );
}
